import React, { useEffect, useRef } from 'react';

import classNames from 'classnames';
import { faMicrophone, faPaperPlane } from '@fortawesome/pro-regular-svg-icons';
import { faStop } from '@fortawesome/pro-solid-svg-icons';
import { Button } from 'components/ui/button';
import { InputGroup } from '@buildoutinc/blueprint-react/ui/InputGroup';
import { Textarea } from '@buildoutinc/blueprint-react/ui/Textarea';
import { AttachmentBadge } from './attachments/badge';
import { AttachmentsInput } from './attachments/input';

interface Props {
  attachments: File[];
  input: string;
  loading: boolean;
  recording: boolean;
  sttSupported: boolean;
  onAddAttachments: (attachments: File[]) => void;
  onInputChange: (value: string) => void;
  onMicClick: () => void;
  onRemoveAttachment: (index: number) => void;
  onSubmit: () => void;
}

export default function MessageInput({
  attachments,
  input,
  loading,
  recording,
  sttSupported,
  onAddAttachments,
  onInputChange,
  onMicClick,
  onRemoveAttachment,
  onSubmit,
}: Props) {
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;

    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [input]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onSubmit();
  };

  // Enter sends; Shift+Enter inserts a newline
  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      onSubmit();
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {attachments.length > 0 && (
        <div className="d-flex flex-wrap gap-1 mb-2">
          {attachments.map((attachment, index) => (
            <AttachmentBadge key={index} name={attachment.name} onRemove={() => onRemoveAttachment(index)} />
          ))}
        </div>
      )}

      <InputGroup className="align-items-end">
        <AttachmentsInput disabled={loading} onAddAttachments={onAddAttachments} />
        <Textarea
          disabled={loading}
          placeholder={recording ? 'Listening…' : 'Ask Otto anything…'}
          ref={inputRef}
          rows={1}
          style={{ resize: 'none', minHeight: 0, maxHeight: '9rem', overflowY: 'auto' }}
          value={input}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onInputChange(e.target.value)}
          onKeyDown={handleInputKeyDown}
        />
        {sttSupported && (
          <Button
            aria-label={recording ? 'Stop recording' : 'Speak to Otto'}
            className={classNames({ 'text-danger': recording })}
            disabled={loading}
            icon={recording ? faStop : faMicrophone}
            variant="ghost"
            onClick={onMicClick}
          />
        )}
        <Button aria-label="Send" disabled={loading || !input.trim()} icon={faPaperPlane} type="submit" />
      </InputGroup>
    </form>
  );
}
